import Checkbox from "../Checkbox";
import ExpandableAddress from "../ExpandableAddress";
import LinedInput, {InputType} from "../LinedInput";
import maskInput from "../../lib/maskInput";
import masks from "../../constants/masks";
import PhoneInput from "../PhoneInput";
import React, {useState, useRef} from "react";
import styles from "./CardAccountInfo.module.css";
import useFocusInvalid from "../../lib/useFocusInvalid";
import {AddressKeys, SecuredDepositKeys} from "../../lib/api/models";
import Select from "../Select";

const strings = {
    addressLabel: "Address",
    cardNameLabel: "Name on Account",
    cardNumberLabel: "Credit/Debit Card Number",
    cardType: "Card Type",
    checkboxLabel: "Save this account info for future use",
    checkboxWarrant: "I warrant I am the cardholder or an authorized user of this account",
    expirationDateLabel: "Expiration Date",
    phoneExtra:
        "By providing your phone number, you consent to our use of automatic dialers, text or pre-recorded or artificial voice messages to service your account. Message and data rates may apply.",
    primaryPhone: "Primary Phone",
};

enum CardType {
    Debit = "DEBIT",
    Credit = "CREDIT",
}

function getCardTypeLabel(cardType: CardType): string {
    switch (cardType) {
        case CardType.Debit:
            return "Debit";
        case CardType.Credit:
            return "Credit";
    }
}

const cardTypeArr = [CardType.Debit, CardType.Credit];

const cardTypeLabelsArr = cardTypeArr.map((cardType: CardType) => getCardTypeLabel(cardType));

export const MAX_CARD_LENGTH = 16;

type Props = {
    onChange: (name: SecuredDepositKeys | AddressKeys, value: string) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
    cardName: string;
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    zipcode: string;
    onChangeAddress: (field: AddressKeys, value: string) => void;
    cardNumber: string;
    expirationDate: string;
    phoneNumber: string;
    isChecked: boolean;
    setIsChecked: (b: boolean) => void;
    isWarrantChecked: boolean;
    setIsWarrantChecked: (b: boolean) => void;
    firstInvalidKey?: string;
    validationCount: number;
    validationCardName?: string[];
    validationLine1?: string[];
    validationLine2?: string[];
    validationCity?: string[];
    validationState?: string[];
    validationZipcode?: string[];
    validationCardNumber?: string[];
    validationCardType?: string[];
    validationExpirationDate?: string[];
    validationPhoneNumber?: string[];
    className?: string;
};

function maskExpirationDateInput(input: string): string {
    const maskedValue = maskInput(masks.cardExpirationDate.pattern, input);

    let result = "";
    const dateParts = maskedValue.split("/");
    const month = dateParts[0];

    // Month Validation
    if (month) {
        if (month.length > 1) {
            const monthInt = parseInt(month, 10);

            if (monthInt >= 1 && monthInt <= 12) {
                result += month;
            } else {
                return maskInput(masks.dateOfBirth.pattern, result + month[0]);
            }
        } else {
            const monthInt = parseInt(month, 10);

            if (monthInt >= 0 && monthInt <= 1) {
                return maskInput(masks.dateOfBirth.pattern, (result += monthInt));
            } else {
                return maskInput(masks.dateOfBirth.pattern, result);
            }
        }
    }

    return maskedValue; // Input valid as is so return.
}

export default function CardAccountInfo(props: Props) {
    const [isAddressExpanded, setIsAddressExpanded] = useState(false);

    function handleChangeWithMask(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        props.onChange(SecuredDepositKeys.CardExpirationDate, maskExpirationDateInput(value));
    }

    /* Focus first failing validation */
    const cardNameRef = useRef<HTMLInputElement>(null);
    const cardNumberRef = useRef<HTMLInputElement>(null);
    const cardTypeRef = useRef<HTMLSelectElement>(null);
    const cardExpirationRef = useRef<HTMLInputElement>(null);
    const phoneNumberRef = useRef<HTMLInputElement>(null);

    const [cardTypeIndex, setCardTypeIndex] = useState(-1);

    let invalidRef;
    if (props.firstInvalidKey === SecuredDepositKeys.CardName) {
        invalidRef = cardNameRef;
    } else if (props.firstInvalidKey === SecuredDepositKeys.CardNumber) {
        invalidRef = cardNumberRef;
    } else if (props.firstInvalidKey === SecuredDepositKeys.CardExpirationDate) {
        invalidRef = cardExpirationRef;
    }
    useFocusInvalid(props.validationCount, invalidRef?.current);

    return (
        <div className={props.className}>
            <LinedInput
                id={SecuredDepositKeys.CardName}
                name={SecuredDepositKeys.CardName}
                onBlur={props.onBlur}
                ref={cardNameRef}
                required={true}
                value={props.cardName}
                setValue={(value) => props.onChange(SecuredDepositKeys.CardName, value)}
                label={strings.cardNameLabel}
                type={InputType.Text}
                validationMessage={props.validationCardName}
            />
            <ExpandableAddress
                line1={props.line1}
                line2={props.line2}
                city={props.city}
                state={props.state}
                zipcode={props.zipcode}
                onBlurInput={props.onBlur}
                onChangeAddress={props.onChangeAddress}
                isExpanded={isAddressExpanded}
                setIsExpanded={(isExpanded: boolean) => setIsAddressExpanded(isExpanded)}
                showLine1ExtraInfo={false}
                firstInvalidKey={props.firstInvalidKey}
                validationCount={props.validationCount}
                validationLine1={props.validationLine1}
                validationLine2={props.validationLine2}
                validationCity={props.validationCity}
                validationState={props.validationState}
                validationZipcode={props.validationZipcode}
            />
            <LinedInput
                id={SecuredDepositKeys.CardNumber}
                name={SecuredDepositKeys.CardNumber}
                ref={cardNumberRef}
                required={true}
                value={props.cardNumber}
                setValue={(value) => props.onChange(SecuredDepositKeys.CardNumber, value)}
                onBlur={props.onBlur}
                label={strings.cardNumberLabel}
                type={InputType.Text}
                minLength={MAX_CARD_LENGTH}
                maxLength={MAX_CARD_LENGTH}
                validationMessage={props.validationCardNumber}
            />
            <LinedInput
                id={SecuredDepositKeys.CardExpirationDate}
                name={SecuredDepositKeys.CardExpirationDate}
                ref={cardExpirationRef}
                required={true}
                value={props.expirationDate}
                onChange={handleChangeWithMask}
                onBlur={props.onBlur}
                label={strings.expirationDateLabel}
                maxLength={masks.cardExpirationDate.pattern.length}
                pattern={masks.cardExpirationDate.regex}
                type={InputType.Text}
                validationMessage={props.validationExpirationDate}
            />
            <PhoneInput
                id={SecuredDepositKeys.PhoneNumber}
                name={SecuredDepositKeys.PhoneNumber}
                label={strings.primaryPhone}
                ref={phoneNumberRef}
                onBlur={props.onBlur}
                value={props.phoneNumber}
                setValue={(value) => props.onChange(SecuredDepositKeys.PhoneNumber, value)}
                extraInfo={strings.phoneExtra}
                required={true}
                validationMessage={props.validationPhoneNumber}
                inputMode="numeric"
            />
            <Select
                id={SecuredDepositKeys.CardType}
                name={SecuredDepositKeys.CardType}
                label={strings.cardType}
                ref={cardTypeRef}
                options={cardTypeLabelsArr}
                onBlur={props.onBlur}
                selectedIndex={cardTypeIndex}
                setSelected={(index) => {
                    setCardTypeIndex(index);
                    props.onChange(SecuredDepositKeys.CardType, index ? cardTypeArr[index] : "");
                }}
                required={true}
                validationMessage={props.validationCardType}
            />
            <Checkbox
                className={styles.checkbox}
                isChecked={props.isChecked}
                onChange={(e) => props.setIsChecked(e.target.checked)}
                label={strings.checkboxLabel}
            />
            <Checkbox
                className={styles.checkbox}
                isChecked={props.isWarrantChecked}
                onChange={(e) => props.setIsWarrantChecked(e.target.checked)}
                label={strings.checkboxWarrant}
                required={true}
            />
        </div>
    );
}
